import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box, Tooltip } from "@material-ui/core";
import { ErrorOutline } from "@material-ui/icons";

import FileDownload from "./FileDownload";
import { useTranslation } from "../hooks/useTranslation";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0.25),
    minWidth: 120,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  resultCard: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.spacing(0.5),
    border: "1px solid var(--ikaro-purple-light)",
    backgroundColor: "var(--ikaro-purple-light)",
    transition: "background-color 0.12s ease, border-color 0.12s ease",
    /* Sobrescreve as cores do FileDownload interno para alinhar com a paleta
       de "saída" (roxo), diferenciando visualmente do "entrada" (laranja).
       No light mode usamos o roxo da marca (cont​raste OK sobre o fundo
       claro do chip). No dark mode o roxo escuro fica ilegível sobre o
       fundo escuro translúcido, então trocamos por branco + lilás claro
       para manter a legibilidade. */
    "& .MuiSvgIcon-root": {
      color: "var(--ikaro-purple-accent)",
    },
    "& span[class*=fileName]": {
      color: "var(--ikaro-purple-accent)",
    },
    "&:hover": {
      backgroundColor: "rgba(100, 49, 132, 0.18)",
      borderColor: "var(--ikaro-purple-accent)",
    },
    '[data-theme="dark"] &': {
      "& .MuiSvgIcon-root": {
        color: "var(--ikaro-text-white)",
      },
      "& span[class*=fileName]": {
        color: "var(--ikaro-text-white)",
      },
      "&:hover": {
        backgroundColor: "rgba(196, 164, 224, 0.22)",
        borderColor: "#c4a4e0",
      },
    },
  },
  /* Indicador discreto de erro (ícone vermelho com tooltip). Sem caixa de
     fundo nem texto longo: o status chip da linha já comunica o erro; aqui
     basta um sinal sutil de "documento indisponível". */
  errorIndicator: {
    display: "inline-flex",
    alignItems: "center",
    color: "var(--ikaro-status-error-text)",
    cursor: "help",
    "& .MuiSvgIcon-root": {
      fontSize: "1.05rem",
    },
  },
  /* Placeholder neutro usado quando não há resultado nem erro (ex.: job
     ainda em processamento ou descartado). Mantém a coluna alinhada
     visualmente sem chamar a atenção. */
  emptyDash: {
    color: "var(--ikaro-text-muted)",
    fontSize: "0.95rem",
    lineHeight: 1,
    paddingLeft: theme.spacing(0.25),
    userSelect: "none",
  },
}));

function normalizeOutputPayload(outputPayload) {
  if (!outputPayload) return null;
  if (typeof outputPayload === "string") {
    try {
      const p = JSON.parse(outputPayload);
      return p && typeof p === "object" ? p : null;
    } catch {
      return null;
    }
  }
  return typeof outputPayload === "object" ? outputPayload : null;
}

const ERROR_STATUSES = new Set(["ERROR", "FINAL_ERROR", "ERROR_FINALIZING"]);

const ContestacaoOutputDisplay = ({ outputPayload, status, job }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const payload = normalizeOutputPayload(outputPayload);

  const resultFiles = payload?.result_files;
  const resultDocx = payload?.result_docx;
  const hasFiles = resultFiles?.length || resultDocx;

  /* Quando há resultado (intermediário ou final), apresenta os arquivos.
     Caso contrário, deixa em branco — o status já comunica em qual etapa
     o job está, sem precisar de mensagem repetida nesta coluna.
     Erros recebem um pequeno indicador para o usuário não confundir
     "sem documento por estar em processamento" com "falhou". */
  if (hasFiles) {
    if (resultFiles?.length) {
      return (
        <Box className={classes.container}>
          {resultFiles.map((f, i) => (
            <Box key={i} className={classes.resultCard}>
              <FileDownload
                file={f}
                previewType={f.filename?.endsWith(".pdf") ? "pdf" : "docx"}
              />
            </Box>
          ))}
        </Box>
      );
    }
    return (
      <Box className={classes.container}>
        <Box className={classes.resultCard}>
          <FileDownload
            file={resultDocx}
            html_preview_token={resultDocx.html_preview_token}
            previewType="docx"
          />
        </Box>
      </Box>
    );
  }

  const upper = (status || "").toUpperCase();
  if (ERROR_STATUSES.has(upper)) {
    return (
      <Box className={classes.container}>
        <Tooltip
          title={t("contestacao.outputDisplay.empty.error")}
          arrow
          placement="top"
        >
          <Box className={classes.errorIndicator}>
            <ErrorOutline />
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography component="span" className={classes.emptyDash}>
        —
      </Typography>
    </Box>
  );
};

export default ContestacaoOutputDisplay;
